import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { FormFieldDropdown } from '~/components/FormField';
import { UserRoleMap } from '~settings/constants/UserRoleMap';
import type { UserTypeMap } from '~settings/constants/UserTypeMap';
import { UserRoleInfoModal } from '~settings/modules/user/components/UserForm/components/UserRoleInfoModal';

import { useUserRoles } from '../../api/queries/userRoles';

interface SelectUserRoleProps
  extends Omit<ComponentProps<typeof FormFieldDropdown>, 'label' | 'options'>,
    Partial<Pick<ComponentProps<typeof FormFieldDropdown>, 'label'>> {
  targetUserType?: keyof typeof UserTypeMap;
}

const mapRoles = (
  roles: readonly { id: string; name: string }[],
  t: (key: string) => string,
) =>
  roles
    .map((role) => ({
      value: role.name,
      label: t(`${UserRoleMap[role.name as keyof typeof UserRoleMap]}.name`),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

export function SelectUserRole({
  multiselect = false,
  name,
  targetUserType,
  ...props
}: SelectUserRoleProps) {
  const { t } = useTranslation('settings', { keyPrefix: 'dropdowns.role' });
  const { t: rolesT } = useTranslation('settings', { keyPrefix: 'roles' });

  const { userRoles, isLoading } = useUserRoles({ targetUserType });
  const availableRoles =
    userRoles?.data?.filter((role) =>
      Object.keys(UserRoleMap).includes(role.name),
    ) ?? [];

  const count = multiselect ? 2 : 1;

  return (
    <FormFieldDropdown
      label={t('title', { count })}
      placeholder={t('placeholder', { count })}
      {...props}
      loading={isLoading}
      multiselect={multiselect}
      name={name}
      options={mapRoles(availableRoles, rolesT)}
      readOnly={isLoading || props.readOnly}
      labelAddon={<UserRoleInfoModal availableUserRoles={availableRoles} />}
    />
  );
}

SelectUserRole.displayName = 'SelectUserRole';
